"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const PolicyCategoryContext = createContext({
  categories: [],
});

export const PolicyCategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policy-categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch categories");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setCategories(data.data || []);
      } else {
        setError(data.message || "Failed to fetch categories");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  // Create Category
  const createCategory = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policy-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Create failed",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchCategories();

        toast.success(data.message || "Category created successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Create failed",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Create failed",
      };
    }
  };

  // Update Category
  const updateCategory = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policy-categories/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update category",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchCategories();

        toast.success(data.message || "Category updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update category",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update category",
      };
    }
  };

  // Delete Category
  const deleteCategory = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policy-categories/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete category",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchCategories();

        toast.success(data.message || "Category deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete category",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete category",
      };
    }
  };

  // Update Status
  const updateStatus = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policy-categories/status/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchCategories();

        toast.success(data.message || "Category status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update status",
      };
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) fetchCategories();
  }, []);

  return (
    <PolicyCategoryContext.Provider
      value={{
        categories,
        loading,
        error,
        fetchCategories,
        createCategory,
        updateCategory,
        deleteCategory,
        setCategories,
        updateStatus,
      }}
    >
      {children}
    </PolicyCategoryContext.Provider>
  );
};

export const usePolicyCategories = () =>
  useContext(PolicyCategoryContext);